import { useState } from 'react';
import { ErrorMsg, Time } from '../../components/ui';
import { api, errorText, newIdempotencyKey } from '../../lib/api';
import type { ImageLayer, ItemDetail, Layer, Layout, LogoLayer, Revision, ShapeLayer, StyleName, TextLayer } from '../../lib/contentTypes';
import { useContentMeta } from './common';
import { newId, type EditorApi } from './Editor';
import { ensureFonts, BUNDLED_FAMILIES } from './fonts';
import { MotionDialog } from './Motion';
import { canvasBlob, renderToCanvas, type RenderEnv } from './render';
import { useVideoMeta } from './Videos';
import { buildArtboard, restyle, SLIDE_STYLES } from './slideStyles';

const STYLES: StyleName[] = ['title', 'subtitle', 'body', 'caption', 'label'];

function Num({ label, value, onChange, step = 1, min, max, disabled }: { label: string; value: number; onChange: (v: number) => void; step?: number; min?: number; max?: number; disabled?: boolean }) {
  return (
    <label className="field cs-num">
      <span>{label}</span>
      <input type="number" value={Number.isFinite(value) ? Math.round(value * 1000) / 1000 : ''} step={step} min={min} max={max} disabled={disabled} onChange={(e) => e.target.value !== '' && onChange(Number(e.target.value))} />
    </label>
  );
}

function Swatches({ api: e, value, onChange, label }: { api: EditorApi; value: string; onChange: (hex: string) => void; label: string }) {
  return (
    <div className="cs-swatches" role="group" aria-label={label}>
      {e.brand.spec.colors.map((c) => (
        <button key={c.name + c.hex} type="button" className={`cs-swatch${c.hex.toLowerCase() === value.toLowerCase() ? ' on' : ''}`} style={{ background: c.hex }} title={`${c.name} ${c.hex}`} aria-label={`${label}: ${c.name}`} onClick={() => onChange(c.hex)} disabled={e.readOnly} />
      ))}
      <input type="color" value={value} onChange={(ev) => onChange(ev.target.value)} aria-label={`${label}: custom`} disabled={e.readOnly} />
    </div>
  );
}

// --- artboards and layers --------------------------------------------------------------------

export function ArtboardPanel({ api: e }: { api: EditorApi }) {
  const { layout, sel, readOnly } = e;
  const board = layout.artboards[sel.ab];
  const carousel = e.format === 'carousel';
  const [newStyle, setNewStyle] = useState(SLIDE_STYLES[0].key);
  const [changeStyle, setChangeStyle] = useState(SLIDE_STYLES[0].key);
  const [styleMsg, setStyleMsg] = useState('');
  const [pano, setPano] = useState({ asset: '', n: 2 });
  const [cueLabel, setCueLabel] = useState(true);
  const styleOf = (key: string) => SLIDE_STYLES.find((x) => x.key === key)!;
  const brand = e.brand.spec;
  const template = e.mode === 'template';
  // in a template an image box is a numbered slot: the 1st image chosen for an item fills slot 1, ...
  const commit: EditorApi['commit'] = (fn, key) =>
    e.commit((l) => {
      fn(l);
      if (template) numberSlots(l);
    }, key);
  const remaining = layout.artboards.length - sel.ab; // slides from the current one to the last
  const panoN = Math.min(Math.max(pano.n, 2), Math.max(remaining, 2));
  const imageAssets = e.allAssets.filter((a) => a.kind === 'image').sort((a, b) => Number(b.project_id === e.projectId) - Number(a.project_id === e.projectId));
  const cues = layout.artboards.some((a) => a.layers.some((x) => (x.name ?? '').startsWith(CUE)));
  const addLayer = (layer: Omit<Layer, 'id'>) => {
    const id = newId('l');
    commit((l) => l.artboards[sel.ab].layers.push({ ...layer, id } as Layer));
    e.setSel({ ab: sel.ab, layer: id });
  };
  const m = (layout.safe_margin_pct / 100) * layout.width;
  const W = layout.width;
  const H = layout.height;
  return (
    <div className="cs-panel">
      {carousel && (
        <>
          <h3 className="cs-h">Slides</h3>
          <ol className="plain cs-slides">
            {layout.artboards.map((a, i) => (
              <li key={a.id}>
                <button type="button" className={`cs-slide${i === sel.ab ? ' on' : ''}`} onClick={() => e.setSel({ ab: i, layer: null })} aria-current={i === sel.ab}>
                  {i + 1}. {a.name || 'Slide'}
                </button>
              </li>
            ))}
          </ol>
          <div className="row gap-s wrap">
            <button
              type="button"
              className="btn small"
              disabled={readOnly || layout.artboards.length >= 20}
              onClick={() => {
                commit((l) => {
                  const copy = structuredClone(l.artboards[sel.ab]);
                  copy.id = newId('a');
                  copy.name = `${copy.name || 'Slide'} copy`;
                  copy.layers.forEach((x) => (x.id = newId('l')));
                  l.artboards.splice(sel.ab + 1, 0, copy);
                  l.meta.alt_text.splice(sel.ab + 1, 0, '');
                });
                e.setSel({ ab: sel.ab + 1, layer: null });
              }}
            >
              Duplicate slide
            </button>
            <button type="button" className="btn small" disabled={readOnly || sel.ab === 0} onClick={() => moveSlide(e, -1)} aria-label="Move slide earlier">
              ↑
            </button>
            <button type="button" className="btn small" disabled={readOnly || sel.ab === layout.artboards.length - 1} onClick={() => moveSlide(e, 1)} aria-label="Move slide later">
              ↓
            </button>
            <button
              type="button"
              className="btn ghost small"
              disabled={readOnly || layout.artboards.length <= 1}
              onClick={() => {
                commit((l) => {
                  l.artboards.splice(sel.ab, 1);
                  l.meta.alt_text.splice(sel.ab, 1);
                });
                e.setSel({ ab: Math.max(0, sel.ab - 1), layer: null });
              }}
            >
              Delete slide
            </button>
          </div>
        </>
      )}
      <h3 className="cs-h">{carousel ? 'Slide style' : 'Page style'}</h3>
      <fieldset disabled={readOnly} className="cs-fieldset cs-styles">
        {carousel && (
          <div className="row gap-s wrap center">
            <select value={newStyle} onChange={(ev) => setNewStyle(ev.target.value)} aria-label="Style of the new slide (it goes after the current slide)" title="The new slide goes after the current one">
              {SLIDE_STYLES.map((x) => (
                <option key={x.key} value={x.key}>
                  {x.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn small"
              disabled={layout.artboards.length >= 20}
              onClick={() => {
                const ab = buildArtboard(styleOf(newStyle), layout, brand, newId);
                commit((l) => {
                  l.artboards.splice(sel.ab + 1, 0, ab);
                  while (l.meta.alt_text.length < l.artboards.length - 1) l.meta.alt_text.push('');
                  l.meta.alt_text.splice(sel.ab + 1, 0, '');
                });
                setStyleMsg('');
                e.setSel({ ab: sel.ab + 1, layer: null });
              }}
            >
              Add slide
            </button>
          </div>
        )}
        <div className="row gap-s wrap center">
          <select value={changeStyle} onChange={(ev) => setChangeStyle(ev.target.value)} aria-label="Style for this page">
            {SLIDE_STYLES.map((x) => (
              <option key={x.key} value={x.key}>
                {x.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              const r = restyle(board, styleOf(changeStyle), layout, brand, newId);
              commit((l) => (l.artboards[sel.ab] = r.artboard));
              setStyleMsg(r.hidden ? `Style changed. ${r.hidden} layer${r.hidden > 1 ? 's' : ''} had no place in it and ${r.hidden > 1 ? 'are' : 'is'} kept hidden (tick “hidden” in the layer to bring ${r.hidden > 1 ? 'one' : 'it'} back).` : 'Style changed. Every image and text was placed.');
              e.setSel({ ab: sel.ab, layer: null });
            }}
          >
            Change style
          </button>
        </div>
        <p className="small muted">{styleOf(changeStyle).description} Images and texts are moved, never deleted.</p>
        {styleMsg && (
          <p className="small msg info" role="status" data-testid="style-msg">
            {styleMsg}
          </p>
        )}
      </fieldset>
      {carousel && (
        <>
          <h3 className="cs-h">Across slides</h3>
          <fieldset disabled={readOnly} className="cs-fieldset cs-across">
            <p className="small muted">One image cut across several slides, so it lines up when people swipe.</p>
            <label className="field">
              <span>Panorama image</span>
              <select value={pano.asset} onChange={(ev) => setPano({ ...pano, asset: ev.target.value })} aria-label="Panorama image">
                <option value="">{template ? 'no picture: an Image slot' : 'choose an image…'}</option>
                {imageAssets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.filename} ({a.width}×{a.height})
                  </option>
                ))}
              </select>
            </label>
            <div className="row gap-s wrap center">
              <label className="field cs-num">
                <span>Slides</span>
                <input type="number" min={2} max={Math.max(remaining, 2)} value={panoN} onChange={(ev) => setPano({ ...pano, n: Number(ev.target.value) || 2 })} aria-label="Slides the panorama covers" />
              </label>
              <button
                type="button"
                className="btn small"
                disabled={(!pano.asset && !template) || remaining < 2}
                title={remaining < 2 ? 'Select a slide that is followed by at least one more' : undefined}
                onClick={() => {
                  const id = newId('l');
                  commit((l) => {
                    const layer: ImageLayer = { id, type: 'image', name: 'Panorama', x: 0, y: 0, w: panoN * l.width, h: l.height, asset_id: pano.asset || null, fit: 'fill', focal_x: 0.5, focal_y: 0.5, crop: { x: 0, y: 0, w: 1, h: 1 }, span: true };
                    l.artboards[sel.ab].layers.unshift(layer);
                  });
                  e.setSel({ ab: sel.ab, layer: id });
                }}
              >
                Create panorama from this slide
              </button>
            </div>
            <div className="row gap-s wrap center">
              <button type="button" className="btn small" disabled={layout.artboards.length < 2} onClick={() => commit((l) => addSwipeCues(l, brand, e.brand.spec.colors, cueLabel))}>
                Add swipe cue
              </button>
              <label className="check small">
                <input type="checkbox" checked={cueLabel} onChange={(ev) => setCueLabel(ev.target.checked)} /> with a “Swipe” label
              </label>
              <button type="button" className="btn ghost small" disabled={!cues} onClick={() => commit((l) => removeSwipeCues(l))}>
                Remove all swipe cues
              </button>
            </div>
          </fieldset>
        </>
      )}
      <h3 className="cs-h">Artboard</h3>
      <fieldset disabled={readOnly} className="cs-fieldset">
        <label className="field">
          <span>Name</span>
          <input value={board.name ?? ''} onChange={(ev) => commit((l) => (l.artboards[sel.ab].name = ev.target.value || null), `abname:${board.id}`)} />
        </label>
        <div className="field">
          <span>Background</span>
          <Swatches api={e} label="Background" value={board.background} onChange={(hex) => commit((l) => (l.artboards[sel.ab].background = hex), `abbg:${board.id}`)} />
        </div>
        <h3 className="cs-h">Add</h3>
        <div className="row gap-s wrap">
          <button type="button" className="btn small" onClick={() => addLayer({ type: 'text', x: m, y: m, w: W - 2 * m, h: H * 0.1, text: 'New text', style: 'subtitle', color: e.brand.spec.colors[0]?.hex ?? '#1c1b19', align: 'left', valign: 'top' } as Omit<TextLayer, 'id'>)}>
            Text
          </button>
          <button type="button" className="btn small" onClick={() => addLayer({ type: 'image', x: m, y: m, w: W - 2 * m, h: H * 0.5, asset_id: null, fit: 'fill', focal_x: 0.5, focal_y: 0.5, crop: { x: 0, y: 0, w: 1, h: 1 } } as Omit<ImageLayer, 'id'>)}>
            Image
          </button>
          <button type="button" className="btn small" onClick={() => addLayer({ type: 'logo', x: W - m - W * 0.2, y: H - m - H * 0.06, w: W * 0.2, h: H * 0.06 })}>
            Logo
          </button>
          <button type="button" className="btn small" onClick={() => addLayer({ type: 'shape', shape: 'square', x: m, y: m, w: W * 0.022, h: W * 0.022, color: accent(e) } as Omit<ShapeLayer, 'id'>)}>
            Square accent
          </button>
          <button type="button" className="btn small" onClick={() => addLayer({ type: 'shape', shape: 'rect', x: 0, y: H * 0.85, w: W, h: H * 0.15, color: e.brand.spec.colors[1]?.hex ?? '#f5f4f0' } as Omit<ShapeLayer, 'id'>)}>
            Rectangle
          </button>
          <button type="button" className="btn small" onClick={() => addLayer({ type: 'shape', shape: 'arrow', x: W - m - W * 0.04, y: H - m - W * 0.05, w: W * 0.04, h: W * 0.05, color: accent(e) } as Omit<ShapeLayer, 'id'>)}>
            Arrow
          </button>
          <button type="button" className="btn small" onClick={() => addLayer({ type: 'shape', shape: 'line', x: m, y: H / 2, w: W - 2 * m, h: Math.max(2, W * 0.003), color: e.brand.spec.colors[0]?.hex ?? '#1c1b19' } as Omit<ShapeLayer, 'id'>)}>
            Line
          </button>
        </div>
      </fieldset>
      <h3 className="cs-h">Layers (top first)</h3>
      <ol className="plain cs-layers">
        {[...board.layers].reverse().map((layer) => (
          <li key={layer.id}>
            <button type="button" className={`cs-layer${e.sel.layer === layer.id ? ' on' : ''}${layer.hidden ? ' hidden' : ''}`} onClick={() => e.setSel({ ab: sel.ab, layer: layer.id })}>
              {layerLabel(layer)}
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

const CUE = 'Swipe cue';

function luminance(hex: string) {
  const v = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * v[0] + 0.7152 * v[1] + 0.0722 * v[2];
}

/** An arrow (and an optional "Swipe" label) at the bottom-right safe margin of every slide but the last. */
function addSwipeCues(l: Layout, brand: { styles: Record<string, { size_pct: number; line_height: number }> }, colors: { name: string; hex: string }[], label: boolean) {
  removeSwipeCues(l);
  const W = l.width;
  const H = l.height;
  const m = (l.safe_margin_pct / 100) * W;
  const aw = W * 0.04;
  const ah = W * 0.05;
  const ink = colors.find((c) => c.name === 'Ink')?.hex ?? '#1c1b19';
  const white = colors.find((c) => c.name === 'White')?.hex ?? '#ffffff';
  l.artboards.slice(0, -1).forEach((a) => {
    const color = luminance(a.background) < 0.5 ? white : ink;
    const x = Math.round((W - m - aw) * 100) / 100;
    const y = Math.round((H - m - ah) * 100) / 100;
    a.layers.push({ id: newId('l'), type: 'shape', name: CUE, shape: 'arrow', x, y, w: Math.round(aw * 100) / 100, h: Math.round(ah * 100) / 100, color } as ShapeLayer);
    if (label) {
      const lh = (brand.styles.label.size_pct / 100) * W * brand.styles.label.line_height;
      const lw = W * 0.14;
      a.layers.push({ id: newId('l'), type: 'text', name: `${CUE} label`, x: Math.round((x - W * 0.015 - lw) * 100) / 100, y: Math.round((y + (ah - lh) / 2) * 100) / 100, w: Math.round(lw * 100) / 100, h: Math.round(lh * 100) / 100, text: 'Swipe', style: 'label', color, align: 'right', valign: 'middle' } as TextLayer);
    }
  });
}

function removeSwipeCues(l: Layout) {
  l.artboards.forEach((a) => (a.layers = a.layers.filter((x) => !(x.name ?? '').startsWith(CUE))));
}

/** Gives every image box that has no slot the lowest free slot number (0-based; at most 21 slots). */
function numberSlots(l: Layout) {
  const used = new Set<number>();
  for (const a of l.artboards) for (const x of a.layers) if (x.type === 'image' && x.slot != null) used.add(x.slot);
  let next = 0;
  for (const a of l.artboards) {
    for (const x of a.layers) {
      if (x.type !== 'image' || x.slot != null) continue;
      while (used.has(next)) next++;
      if (next > 20) return;
      x.slot = next;
      used.add(next);
    }
  }
}

const BINDS: { key: string; label: string }[] = [
  { key: 'title', label: 'the project title' },
  { key: 'location', label: 'the location' },
  { key: 'public_description', label: 'the public description' },
  { key: 'credits', label: 'the credits' },
];

function accent(e: EditorApi) {
  return e.brand.spec.colors.find((c) => /bronze|accent/i.test(c.name))?.hex ?? e.brand.spec.colors[0]?.hex ?? '#8a5a32';
}

function layerLabel(l: Layer) {
  if (l.type === 'text') return `Text · ${l.text.slice(0, 24) || '(empty)'}`;
  if (l.type === 'image') return `Image · ${l.name ?? ''}`;
  if (l.type === 'shape') return `${l.shape === 'square' ? 'Square' : l.shape === 'line' ? 'Line' : l.shape === 'arrow' ? 'Arrow' : 'Rectangle'}${l.name ? ` · ${l.name}` : ''}`;
  return 'Logo';
}

function moveSlide(e: EditorApi, d: number) {
  const i = e.sel.ab;
  e.commit((l) => {
    const [a] = l.artboards.splice(i, 1);
    l.artboards.splice(i + d, 0, a);
    const alt = l.meta.alt_text;
    while (alt.length < l.artboards.length) alt.push('');
    const [t] = alt.splice(i, 1);
    alt.splice(i + d, 0, t);
  });
  e.setSel({ ab: i + d, layer: null });
}

// --- inspector -------------------------------------------------------------------------------

export function Inspector({ api: e }: { api: EditorApi }) {
  const { layout, sel, commit, readOnly } = e;
  const layer = layout.artboards[sel.ab].layers.find((x) => x.id === sel.layer);
  if (!layer) return <div className="cs-panel muted small">Select a layer on the artboard or in the layer list to edit it. Drag it to move, drag a handle to resize (Shift on a corner keeps the proportions, Alt switches snapping off), Ctrl-drag an image to move the picture inside its frame. Arrow keys nudge (Shift: 10 px), Delete removes it.</div>;
  const set = (fn: (l: Layer) => void, key?: string) =>
    commit((d) => {
      const x = d.artboards[sel.ab].layers.find((y) => y.id === layer.id);
      if (x) fn(x);
    }, key ? `${key}:${layer.id}` : undefined);
  const m = (layout.safe_margin_pct / 100) * layout.width;
  const align = (how: string) =>
    set((x) => {
      if (how === 'left') x.x = m;
      if (how === 'center') x.x = (layout.width - x.w) / 2;
      if (how === 'right') x.x = layout.width - m - x.w;
      if (how === 'top') x.y = m;
      if (how === 'middle') x.y = (layout.height - x.h) / 2;
      if (how === 'bottom') x.y = layout.height - m - x.h;
    });
  const idx = layout.artboards[sel.ab].layers.findIndex((x) => x.id === layer.id);
  return (
    <div className="cs-panel" aria-label="Layer properties">
      <h3 className="cs-h">{layerLabel(layer)}</h3>
      <fieldset disabled={readOnly} className="cs-fieldset">
        <div className="cs-grid4">
          <Num label="X" value={layer.x} onChange={(v) => set((x) => (x.x = v), 'x')} />
          <Num label="Y" value={layer.y} onChange={(v) => set((x) => (x.y = v), 'y')} />
          <Num label="W" value={layer.w} min={1} onChange={(v) => set((x) => (x.w = Math.max(1, v)), 'w')} />
          <Num label="H" value={layer.h} min={1} onChange={(v) => set((x) => (x.h = Math.max(1, v)), 'h')} />
        </div>
        <div className="row gap-s wrap" role="group" aria-label="Align to the safe margins">
          {['left', 'center', 'right', 'top', 'middle', 'bottom'].map((a) => (
            <button key={a} type="button" className="btn small" onClick={() => align(a)}>
              {a}
            </button>
          ))}
        </div>
        <div className="row gap-s wrap">
          <button type="button" className="btn small" disabled={idx === layout.artboards[sel.ab].layers.length - 1} onClick={() => commit((d) => swap(d, sel.ab, idx, idx + 1))}>
            Bring forward
          </button>
          <button type="button" className="btn small" disabled={idx === 0} onClick={() => commit((d) => swap(d, sel.ab, idx, idx - 1))}>
            Send back
          </button>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              const id = newId('l');
              commit((d) => d.artboards[sel.ab].layers.push({ ...structuredClone(layer), id, x: layer.x + 20, y: layer.y + 20 }));
              e.setSel({ ab: sel.ab, layer: id });
            }}
          >
            Duplicate
          </button>
          <label className="check small">
            <input type="checkbox" checked={!!layer.hidden} onChange={(ev) => set((x) => (x.hidden = ev.target.checked))} /> hidden
          </label>
          {e.format === 'carousel' && (
            <label className="check small" title="The layer is also drawn on the other slides, shifted by one slide width per slide, below their own layers">
              <input type="checkbox" checked={!!layer.span} onChange={(ev) => set((x) => (x.span = ev.target.checked))} aria-label="Continue on the next slides" /> continue on the next slides
            </label>
          )}
          <button type="button" className="btn ghost small" onClick={() => commit((d) => (d.artboards[sel.ab].layers = d.artboards[sel.ab].layers.filter((x) => x.id !== layer.id)))}>
            Delete
          </button>
        </div>
        {layer.type === 'text' && <TextProps api={e} layer={layer} set={set} />}
        {layer.type === 'image' && <ImageProps api={e} layer={layer} set={set} />}
        {layer.type === 'shape' && (
          <>
            <label className="field">
              <span>Shape</span>
              <select value={layer.shape} onChange={(ev) => set((x) => ((x as ShapeLayer).shape = ev.target.value as ShapeLayer['shape']))}>
                <option value="rect">rectangle</option>
                <option value="square">square (accent)</option>
                <option value="line">line / divider</option>
                <option value="arrow">arrow (swipe cue)</option>
              </select>
            </label>
            <div className="field">
              <span>Colour</span>
              <Swatches api={e} label="Shape colour" value={layer.color} onChange={(hex) => set((x) => ((x as ShapeLayer).color = hex), 'color')} />
            </div>
          </>
        )}
        {layer.type === 'logo' && (
          <>
            <label className="field">
              <span>Logo version</span>
              <select
                value={layer.logo_id ?? ''}
                aria-label="Logo version"
                disabled={e.readOnly}
                onChange={(ev) => set((x) => ((x as LogoLayer).logo_id = ev.target.value || null), 'logo_id')}
              >
                <option value="">Brand default{e.brand.logos.find((v) => v.asset_id === e.brand.logo_asset_id)?.name ? ` (${e.brand.logos.find((v) => v.asset_id === e.brand.logo_asset_id)!.name})` : ''}</option>
                {e.brand.logos.map((v) => (
                  <option key={v.asset_id} value={v.asset_id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </label>
            <p className="small muted">
              {e.brand.logos.length
                ? 'The logo keeps its proportions inside the box.'
                : 'The brand kit has no logo yet: add one under Brand & templates. Approval is blocked while this box is empty.'}
            </p>
          </>
        )}
      </fieldset>
    </div>
  );
}

function swap(d: Layout, ab: number, i: number, j: number) {
  const ls = d.artboards[ab].layers;
  [ls[i], ls[j]] = [ls[j], ls[i]];
}

function TextProps({ api: e, layer, set }: { api: EditorApi; layer: TextLayer; set: (fn: (l: Layer) => void, key?: string) => void }) {
  const st = e.brand.spec.styles[layer.style];
  const t = (fn: (l: TextLayer) => void, key?: string) => set((x) => fn(x as TextLayer), key);
  const applyAll = (what: 'color' | 'size') =>
    e.commit((d) =>
      d.artboards.forEach((a) =>
        a.layers.forEach((x) => {
          if (x.type !== 'text' || x.style !== layer.style) return;
          if (what === 'color') x.color = layer.color;
          else x.size = layer.size ?? null;
        }),
      ),
    );
  return (
    <>
      <label className="field">
        <span>Text</span>
        <textarea rows={3} value={layer.text} onChange={(ev) => t((x) => (x.text = ev.target.value), 'text')} aria-label="Text content" />
      </label>
      {e.mode === 'template' && (
        <label className="field">
          <span>Fill from the project</span>
          <select value={layer.bind ?? ''} onChange={(ev) => t((x) => (x.bind = ev.target.value || null))} aria-label="Fill from the project">
            <option value="">no: these exact words</option>
            {BINDS.map((b) => (
              <option key={b.key} value={b.key}>
                {b.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {layer.bind && e.mode === 'template' && <p className="small muted">An item made from this template gets the project's public “{layer.bind.replace('_', ' ')}” here (never the client name or internal notes). The words shown are a sample.</p>}
      {layer.bind && e.mode !== 'template' && <p className="small muted">Filled from the project's public “{layer.bind.replace('_', ' ')}” when the item was created. Editing it here changes this item only.</p>}
      <div className="cs-grid4">
        <label className="field">
          <span>Style</span>
          <select value={layer.style} onChange={(ev) => t((x) => (x.style = ev.target.value as StyleName))}>
            {STYLES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Align</span>
          <select value={layer.align} onChange={(ev) => t((x) => (x.align = ev.target.value as TextLayer['align']))}>
            <option value="left">left</option>
            <option value="center">centre</option>
            <option value="right">right</option>
          </select>
        </label>
        <label className="field">
          <span>Vertical</span>
          <select value={layer.valign} onChange={(ev) => t((x) => (x.valign = ev.target.value as TextLayer['valign']))}>
            <option value="top">top</option>
            <option value="middle">middle</option>
            <option value="bottom">bottom</option>
          </select>
        </label>
        <label className="field">
          <span>Size px</span>
          <input
            type="number"
            min={4}
            placeholder={String(Math.round((st.size_pct / 100) * e.layout.width))}
            value={layer.size ?? ''}
            onChange={(ev) => t((x) => (x.size = ev.target.value ? Number(ev.target.value) : null), 'size')}
            aria-label="Size override in pixels (empty: brand style)"
          />
        </label>
      </div>
      <div className="cs-grid4">
        <label className="field">
          <span>Font</span>
          <input list="cs-editor-fonts" placeholder={st.font} value={layer.font ?? ''} onChange={(ev) => t((x) => (x.font = ev.target.value || null), 'font')} aria-label="Font override (empty: brand style)" />
        </label>
        <label className="field">
          <span>Weight</span>
          <input type="number" min={100} max={900} step={100} placeholder={String(st.weight)} value={layer.weight ?? ''} onChange={(ev) => t((x) => (x.weight = ev.target.value ? Number(ev.target.value) : null), 'weight')} />
        </label>
      </div>
      <datalist id="cs-editor-fonts">
        {BUNDLED_FAMILIES.map((f) => (
          <option key={f} value={f} />
        ))}
      </datalist>
      <div className="field">
        <span>Colour</span>
        <Swatches api={e} label="Text colour" value={layer.color} onChange={(hex) => t((x) => (x.color = hex), 'color')} />
      </div>
      <div className="row gap-s wrap">
        <button type="button" className="btn small" onClick={() => applyAll('color')}>
          Apply this colour to every {layer.style}
        </button>
        <button type="button" className="btn small" onClick={() => applyAll('size')}>
          Apply this size to every {layer.style}
        </button>
      </div>
    </>
  );
}

function ImageProps({ api: e, layer, set }: { api: EditorApi; layer: ImageLayer; set: (fn: (l: Layer) => void, key?: string) => void }) {
  const t = (fn: (l: ImageLayer) => void, key?: string) => set((x) => fn(x as ImageLayer), key);
  const size = e.imageSize(layer.asset_id);
  const asset = layer.asset_id ? e.assets.get(layer.asset_id) : undefined;
  const projectId = e.projectId;
  if (e.mode === 'template') {
    return (
      <>
        <label className="field cs-num">
          <span>Image slot</span>
          <input
            type="number"
            min={1}
            max={21}
            value={layer.slot != null ? layer.slot + 1 : ''}
            onChange={(ev) => t((x) => (x.slot = ev.target.value ? Math.min(20, Math.max(0, Math.round(Number(ev.target.value)) - 1)) : null))}
            aria-label="Image slot"
          />
        </label>
        <p className="small muted">The first image chosen when an item is made fills slot 1, the second slot 2… An empty slot is left for the person to choose.</p>
        <div className="row gap wrap">
          <label className="check small">
            <input type="radio" name={`fit-${layer.id}`} checked={layer.fit === 'fill'} onChange={() => t((x) => (x.fit = 'fill'))} /> fill the box
          </label>
          <label className="check small">
            <input type="radio" name={`fit-${layer.id}`} checked={layer.fit === 'fit'} onChange={() => t((x) => (x.fit = 'fit'))} /> fit inside
          </label>
        </div>
      </>
    );
  }
  const choices = e.allAssets.filter((a) => a.kind === 'image').sort((a, b) => Number(b.project_id === projectId) - Number(a.project_id === projectId));
  const scale = size ? (layer.fit === 'fill' ? Math.max(layer.w / (size.w * layer.crop.w), layer.h / (size.h * layer.crop.h)) : Math.min(layer.w / (size.w * layer.crop.w), layer.h / (size.h * layer.crop.h))) : null;
  return (
    <>
      <label className="field">
        <span>Image</span>
        <select value={layer.asset_id ?? ''} onChange={(ev) => t((x) => (x.asset_id = ev.target.value || null))} aria-label="Image asset">
          <option value="">none</option>
          {choices.map((a) => (
            <option key={a.id} value={a.id}>
              {a.filename} ({a.width}×{a.height}){a.project_id === projectId && projectId ? ' · this project' : ''}
            </option>
          ))}
        </select>
      </label>
      {asset && (
        <p className="small muted">
          Original {asset.width}×{asset.height}, unchanged. Drawn from {size ? `${size.w}×${size.h}` : '…'}
          {scale != null && <> at ×{scale.toFixed(2)}{scale > 1.05 ? ' (enlarged: may look soft)' : ''}</>}.
        </p>
      )}
      <div className="row gap wrap">
        <label className="check small">
          <input type="radio" name={`fit-${layer.id}`} checked={layer.fit === 'fill'} onChange={() => t((x) => (x.fit = 'fill'))} /> fill the box
        </label>
        <label className="check small">
          <input type="radio" name={`fit-${layer.id}`} checked={layer.fit === 'fit'} onChange={() => t((x) => (x.fit = 'fit'))} /> fit inside
        </label>
      </div>
      <div className="cs-grid4">
        <Range label="Focal X" value={layer.focal_x} onChange={(v) => t((x) => (x.focal_x = v), 'fx')} />
        <Range label="Focal Y" value={layer.focal_y} onChange={(v) => t((x) => (x.focal_y = v), 'fy')} />
      </div>
      <p className="small muted">Crop (fractions of the image; the file itself is never cut)</p>
      <div className="cs-grid4">
        <Range label="Crop left" value={layer.crop.x} max={1 - layer.crop.w} onChange={(v) => t((x) => (x.crop.x = Math.min(v, 1 - x.crop.w)), 'cx')} />
        <Range label="Crop top" value={layer.crop.y} max={1 - layer.crop.h} onChange={(v) => t((x) => (x.crop.y = Math.min(v, 1 - x.crop.h)), 'cy')} />
        <Range label="Crop width" value={layer.crop.w} min={0.05} max={1 - layer.crop.x} onChange={(v) => t((x) => (x.crop.w = Math.min(v, 1 - x.crop.x)), 'cw')} />
        <Range label="Crop height" value={layer.crop.h} min={0.05} max={1 - layer.crop.y} onChange={(v) => t((x) => (x.crop.h = Math.min(v, 1 - x.crop.y)), 'ch')} />
      </div>
      <button type="button" className="btn small" onClick={() => t((x) => (x.crop = { x: 0, y: 0, w: 1, h: 1 }))}>
        Reset crop
      </button>
    </>
  );
}

function Range({ label, value, onChange, min = 0, max = 1 }: { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  return (
    <label className="field cs-range">
      <span>
        {label} <span className="num muted">{value.toFixed(2)}</span>
      </span>
      <input type="range" min={min} max={Math.max(min, max)} step={0.01} value={value} onChange={(ev) => onChange(Math.round(Number(ev.target.value) * 1000) / 1000)} aria-label={label} />
    </label>
  );
}

// --- copy: captions, alt text, credits ---------------------------------------------------------

export function CopyPanel({ api: e }: { api: EditorApi }) {
  const meta = useContentMeta();
  const { layout, commit, readOnly } = e;
  const channels = Object.keys(layout.meta.captions);
  const [add, setAdd] = useState('');
  return (
    <div className="cs-panel">
      <h3 className="cs-h">Captions and alt text</h3>
      <p className="small muted">Part of the revision: they go into the export package with the images, and editing them after approval revokes it. No AI suggestions (no model provider configured).</p>
      <fieldset disabled={readOnly} className="cs-fieldset">
        <label className="field cs-inline">
          <span>Language</span>
          <select value={layout.meta.language} onChange={(ev) => commit((d) => (d.meta.language = ev.target.value))}>
            <option value="en">English</option>
            <option value="fr">French</option>
            <option value="bg">Bulgarian</option>
          </select>
        </label>
        <label className="field">
          <span>Credits</span>
          <input value={layout.meta.credits ?? ''} onChange={(ev) => commit((d) => (d.meta.credits = ev.target.value || null), 'credits')} />
        </label>
        {layout.artboards.map((a, i) => (
          <label key={a.id} className="field">
            <span>
              Alt text, {layout.artboards.length > 1 ? `slide ${i + 1}` : 'image'}
            </span>
            <input
              value={layout.meta.alt_text[i] ?? ''}
              onChange={(ev) =>
                commit((d) => {
                  while (d.meta.alt_text.length < d.artboards.length) d.meta.alt_text.push('');
                  d.meta.alt_text[i] = ev.target.value;
                }, `alt:${a.id}`)
              }
            />
          </label>
        ))}
        {channels.map((ch) => (
          <div key={ch} className="cs-caption">
            <label className="field">
              <span>Caption · {ch}</span>
              <textarea rows={3} value={layout.meta.captions[ch].text} onChange={(ev) => commit((d) => (d.meta.captions[ch].text = ev.target.value), `cap:${ch}`)} aria-label={`Caption for ${ch}`} />
            </label>
            <label className="field">
              <span>Hashtags · {ch}</span>
              <input
                value={layout.meta.captions[ch].hashtags.join(' ')}
                onChange={(ev) =>
                  commit(
                    (d) =>
                      (d.meta.captions[ch].hashtags = ev.target.value
                        .split(/[\s,]+/)
                        .filter(Boolean)
                        .map((h) => (h.startsWith('#') ? h : `#${h}`))),
                    `tags:${ch}`,
                  )
                }
                aria-label={`Hashtags for ${ch}`}
              />
            </label>
            <button type="button" className="btn ghost small" onClick={() => commit((d) => delete d.meta.captions[ch])}>
              Remove {ch} caption
            </button>
          </div>
        ))}
        <div className="row gap-s center">
          <select value={add} onChange={(ev) => setAdd(ev.target.value)} aria-label="Channel">
            <option value="">add a caption for…</option>
            {meta?.channels
              .filter((c) => !channels.includes(c))
              .map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
          </select>
          <button
            type="button"
            className="btn small"
            disabled={!add}
            onClick={() => {
              commit((d) => (d.meta.captions[add] = { text: '', hashtags: [] }));
              setAdd('');
            }}
          >
            Add caption
          </button>
        </div>
      </fieldset>
    </div>
  );
}

// --- workflow: review, approval, export, publication -----------------------------------------

export function WorkflowPanel({ api: e, item, dirty, isOwner, reload, env, fontsReady }: { api: EditorApi; item: ItemDetail; dirty: boolean; isOwner: boolean; reload: () => Promise<void>; env: RenderEnv | null; fontsReady: boolean }) {
  const { warnings } = e;
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [url, setUrl] = useState('');
  const [date, setDate] = useState('');
  const blocking = warnings.filter((w) => w.blocking);
  const s = item.status;
  const meta = useContentMeta();
  const video = useVideoMeta();
  const [motion, setMotion] = useState(false);

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(label);
    setError('');
    try {
      await fn();
      await reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  }
  const action = (a: string) => run(a, () => api(`/api/content-items/${item.id}/actions/${a}`, { method: 'POST' }));

  async function doExport(format: 'png' | 'jpeg') {
    if (!env || !item.approval) throw new Error('nothing approved to export');
    await ensureFonts();
    let layout = item.revision?.layout;
    if (item.revision?.id !== item.approval.revision_id) {
      layout = (await api<Revision>(`/api/content-items/${item.id}/revisions/${item.approval.revision_number}`)).layout;
    }
    if (!layout) throw new Error('the approved revision could not be loaded');
    const form = new FormData();
    form.append('revision_id', item.approval.revision_id);
    form.append('file_format', format);
    // An export is ONE request and the production proxy refuses bodies above 25 MB with an empty
    // error: say so before sending, instead of after a long upload.
    const limitMb = (meta?.max_request_mb ?? 25) - 1;
    let totalBytes = 0;
    for (let i = 0; i < layout.artboards.length; i++) {
      const { canvas, warnings: w } = renderToCanvas(layout, i, env);
      const stop = w.filter((x) => x.blocking);
      if (stop.length) throw new Error(`cannot export: ${stop.map((x) => x.message).join('; ')}`);
      const blob = await canvasBlob(canvas, format);
      totalBytes += blob.size;
      if (totalBytes > limitMb * 1_000_000) {
        throw new Error(
          format === 'png'
            ? `this export is over ${limitMb} MB as PNG, more than the server accepts in one request: export JPEG instead (or fewer slides at a time)`
            : `this export is over ${limitMb} MB even as JPEG: export fewer slides at a time`,
        );
      }
      form.append('files', blob, `artboard-${i + 1}.${format === 'png' ? 'png' : 'jpg'}`);
    }
    await api(`/api/content-items/${item.id}/exports`, { method: 'POST', form, idempotencyKey: newIdempotencyKey() });
  }

  return (
    <div className="cs-panel cs-workflow">
      <h3 className="cs-h">Review and export</h3>
      {item.approval && (
        <p className="small">
          Approved: revision <span className="num">{item.approval.revision_number}</span> by {item.approval.by ?? 'unknown'}, <Time iso={item.approval.at} />.
        </p>
      )}
      {(s === 'idea' || s === 'draft') && (
        <button type="button" className="btn block" disabled={!!busy || dirty} onClick={() => void action('request_review')}>
          Request review
        </button>
      )}
      {(s === 'idea' || s === 'draft' || s === 'needs_review') && (
        <>
          <button
            type="button"
            className="btn primary block"
            disabled={!!busy || dirty || !isOwner || blocking.length > 0 || !item.revision}
            onClick={() => void run('approve', () => api(`/api/content-items/${item.id}/approve`, { method: 'POST', body: { revision_id: item.revision!.id } }))}
          >
            Approve revision {item.revision?.number}
          </button>
          {!isOwner && <p className="small muted">Only the owner approves.</p>}
          {dirty && <p className="small muted">Wait for the save to finish: approval binds to a saved revision.</p>}
          {blocking.length > 0 && <p className="small err-text">{blocking.length} problem(s) block approval (see the warnings under the preview).</p>}
        </>
      )}
      {s === 'needs_review' && (
        <button type="button" className="btn block" disabled={!!busy} onClick={() => void action('back_to_draft')}>
          Back to draft
        </button>
      )}
      {(s === 'approved' || s === 'exported' || s === 'published') && (
        <div className="row gap-s wrap">
          <button type="button" className="btn primary" disabled={!!busy || !fontsReady} onClick={() => void run('export', () => doExport('png'))}>
            {busy === 'export' ? 'Exporting…' : 'Export PNG'}
          </button>
          <button type="button" className="btn" disabled={!!busy || !fontsReady} onClick={() => void run('export', () => doExport('jpeg'))}>
            Export JPEG
          </button>
        </div>
      )}
      {(s === 'approved' || s === 'exported' || s === 'published') && video?.configured && isOwner && item.project_id && env && (
        <button type="button" className="btn block" disabled={!!busy || !fontsReady} onClick={() => setMotion(true)}>
          Make a motion version
        </button>
      )}
      {motion && env && <MotionDialog item={item} env={env} onClose={() => setMotion(false)} />}
      {s === 'exported' && (
        <div className="stack cs-publish">
          <p className="small muted">Exporting is not publishing. After posting it yourself, record where:</p>
          <label className="field">
            <span>Published URL</span>
            <input type="url" value={url} onChange={(ev) => setUrl(ev.target.value)} placeholder="https://" />
          </label>
          <label className="field">
            <span>Date (empty: now)</span>
            <input type="date" value={date} onChange={(ev) => setDate(ev.target.value)} />
          </label>
          <button
            type="button"
            className="btn block"
            disabled={!!busy || !url}
            onClick={() => void run('publish', () => api(`/api/content-items/${item.id}/publish`, { method: 'POST', body: { url, published_at: date ? `${date}T12:00:00Z` : null } }))}
          >
            Mark as published
          </button>
        </div>
      )}
      {s === 'published' && item.published_url && (
        <p className="small">
          Published <Time iso={item.published_at} />: <a href={item.published_url}>{item.published_url}</a>
        </p>
      )}
      {(s === 'approved' || s === 'exported') && isOwner && (
        <button type="button" className="btn ghost small" disabled={!!busy} onClick={() => void action('revoke_approval')}>
          Revoke approval
        </button>
      )}
      {s === 'archived' ? (
        <button type="button" className="btn small" disabled={!!busy} onClick={() => void action('restore')}>
          Restore from archive
        </button>
      ) : (
        <button type="button" className="btn ghost small" disabled={!!busy} onClick={() => void action('archive')}>
          Archive
        </button>
      )}
      <ErrorMsg>{error}</ErrorMsg>
      {item.exports.length > 0 && (
        <>
          <h3 className="cs-h">Exports</h3>
          <ul className="plain cs-exports">
            {item.exports.map((x) => (
              <li key={x.id}>
                <span className="small">
                  Revision {x.revision_number} · {x.file_format.toUpperCase()} · <Time iso={x.created_at} />
                </span>
                <a href={x.package.url} className="small" download>
                  Package (.zip with captions)
                </a>
                <span className="small">
                  {x.files.map((f, i) => (
                    <a key={f.name} href={f.url} download className="cs-file-link" data-sha256={f.sha256} data-size={`${f.width}x${f.height}`}>
                      {i + 1}
                    </a>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function RevisionsPanel({ item, viewing, onView, onRestore }: { api: EditorApi; item: ItemDetail; viewing: Revision | null; onView: (n: number) => void; onRestore: (n: number) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="cs-panel">
      <button type="button" className="link cs-h" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        Revisions ({item.revisions.length}) and approvals
      </button>
      {open && (
        <>
          <ol className="plain cs-revisions">
            {item.revisions.slice(0, 30).map((r) => (
              <li key={r.id} className={viewing?.number === r.number ? 'on' : ''}>
                <span className="num">#{r.number}</span> <Time iso={r.created_at} relative />
                {r.note && <span className="muted small"> · {r.note}</span>}
                {r.warnings.length > 0 && <span className="muted small"> · {r.warnings.length} warning(s)</span>}{' '}
                <button type="button" className="link small" onClick={() => onView(r.number)}>
                  view
                </button>
                {r.number !== item.revision?.number && (
                  <button type="button" className="link small" onClick={() => onRestore(r.number)}>
                    restore as new revision
                  </button>
                )}
              </li>
            ))}
          </ol>
          {item.approval_history.length > 0 && (
            <ul className="plain small cs-revisions">
              {item.approval_history.map((a, i) => (
                <li key={i}>
                  Approved #{a.revision_number} by {a.by ?? '?'} <Time iso={a.at} relative />
                  {a.revoked_at && <span className="muted"> · revoked: {a.revoked_reason}</span>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
